import React, { useEffect, useState } from 'react';
import { X, Calendar, Clock, CheckCircle2, ArrowRight } from 'lucide-react';

/**
 * "Ask Peter" booking modal, a 20-minute delivery-health call, not a demo.
 * Framed as a conversation, not a sales pitch (Naomi-vibe: invitational,
 * founder-forward). Captures a lightweight lead form and posts to the
 * existing /api/investors/request endpoint (repurposed as a general lead)
 * or the newsletter/leads endpoint if that exists. For now it just posts
 * to /api/leads/consultation which we'll wire on the backend next.
 */
const AskPeterModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', pain: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/leads/consultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'ask-peter-modal' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError('Sorry, something went wrong. Email peter@keystone.co directly.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-[videra-fade-in_.2s_ease-out]"
      onClick={onClose}
      data-testid="ask-peter-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-[videra-pop-in_.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white p-8">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            data-testid="ask-peter-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-200 mb-3">A 20-minute delivery health check</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight leading-tight">
            Talk to Peter Fulford, no sales pitch, just your project pain.
          </h2>
          <div className="mt-4 flex items-center gap-4 text-sm text-blue-100">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> 20 min</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Reply within 24h</span>
          </div>
        </div>

        <div className="p-8">
          {status === 'sent' ? (
            <div className="text-center py-8" data-testid="ask-peter-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-2">Thanks {form.name?.split(' ')[0] || 'there'}, Peter will be in touch.</h3>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                You'll get a calendar link within 24 hours. If it's urgent, email <a href="mailto:peter@keystone.co" className="text-blue-600 underline">peter@keystone.co</a>.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600 mb-5">
                Ask Peter about:
                <span className="mt-2 flex flex-col gap-1.5 text-slate-800">
                  <span className="inline-flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-blue-600" /> Getting a stalled project moving again</span>
                  <span className="inline-flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-blue-600" /> Connecting delivery, lifecycle and evidence</span>
                  <span className="inline-flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-blue-600" /> Whether Videra fits your world (or shouldn't)</span>
                </span>
              </p>

              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required placeholder="Your name" value={form.name} onChange={update('name')} data-testid="ask-peter-name" className="border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  <input required type="email" placeholder="Work email" value={form.email} onChange={update('email')} data-testid="ask-peter-email" className="border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  <input placeholder="Company" value={form.company} onChange={update('company')} data-testid="ask-peter-company" className="border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  <input placeholder="Role (e.g. Delivery Director)" value={form.role} onChange={update('role')} data-testid="ask-peter-role" className="border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <textarea placeholder="What's the delivery pain you'd like to talk through? (optional)" rows={3} value={form.pain} onChange={update('pain')} data-testid="ask-peter-pain" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                {error && <p className="text-sm text-red-600" data-testid="ask-peter-error">{error}</p>}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  data-testid="ask-peter-submit"
                  className="w-full bg-slate-900 text-white px-6 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                >
                  {status === 'sending' ? 'Sending…' : (<>Request the call <ArrowRight className="w-4 h-4" /></>)}
                </button>
                <p className="text-[11px] text-slate-500 text-center">Peter reads every request personally. No newsletter. No follow-up funnel.</p>
              </form>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes videra-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes videra-pop-in {
          from { opacity: 0; transform: translateY(20px) scale(.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default AskPeterModal;
